import { z } from 'zod';
import { apiGet, apiSend } from '@/lib/api';

/**
 * DOZWOLONE OPERACJE SYSTEMOWE — SPS `UserTransactions` (osoba) i `UserGroupTransactions` (grupa).
 *
 * ⚠️ SŁOWNIK KODÓW POCHODZI Z BAZY KLIENTA, nie z portalu. Tabela `Transactions` ma JEDNĄ kolumnę
 * i ani jednego opisu (49 kodów na `metpol_master`), więc lista dozwolonych operacji jest per
 * instalacja, a nazwy po polsku mamy tylko dla potwierdzonych — patrz `lib/kody-operacji.ts`.
 */
const OperacjeSchema = z.object({
  cel: z.object({
    id: z.string(),
    /** Numer karty (osoba) albo kod grupy — tym SPS adresuje wiersz uprawnienia. */
    kod: z.string(),
    nazwa: z.string(),
  }),
  /** Cały słownik wyspy ze znacznikiem nadania — także kody NIEnadane. */
  kody: z.array(
    z.object({
      code: z.string(),
      /** Nadane TEJ osobie wprost — tylko to da się w oknie odznaczyć. */
      nadany: z.boolean(),
      /**
       * NAZWY GRUP, które to samo uprawnienie już nadają.
       *
       * ⚠️ Przy kodzie, nie osobną listą pod spodem — decyzję „odznaczyć czy nie" podejmuje się
       * w wierszu (zgłoszone 2026-08-18). Dla grupy zawsze puste.
       */
      zGrup: z.array(z.string()),
    }),
  ),
  /**
   * Kody nadane, których NIE MA w słowniku wyspy.
   *
   * ⚠️ Pokazujemy je zamiast ukrywać: taki wiersz mógł powstać ręcznie w SPS albo słownik jest
   * czytany rzadziej niż uprawnienia. Ukrycie znaczyłoby, że portal pokazuje mniej uprawnień,
   * niż automat realnie respektuje.
   */
  pozaSlownikiem: z.array(z.string()),
  /** Kody wynikające Z GRUP tej osoby — tutaj się ich nie odbiera. Dla grupy zawsze pusta. */
  zGrup: z.array(z.string()),
});

export type Operacje = z.infer<typeof OperacjeSchema>;

/** Czyj to zestaw uprawnień — decyduje o adresie, nie o kształcie odpowiedzi. */
export type CelOperacji = 'employee' | 'group';

const sciezka = (cel: CelOperacji, id: string) =>
  cel === 'employee' ? `/employees/${id}/operations` : `/employee-groups/${id}/operations`;

export const fetchOperacje = (cel: CelOperacji, id: string, groupId: string) =>
  apiGet(`${sciezka(cel, id)}?groupId=${groupId}`, OperacjeSchema);

/**
 * ⚠️ RÓŻNICA, NIE LISTA DOCELOWA — tak samo jak przy składzie grupy i dostępie do automatów.
 * Lista docelowa sprawia, że dwie osoby na jednym ekranie cicho kasują sobie zmiany.
 */
export const zmienOperacje = (
  cel: CelOperacji,
  id: string,
  payload: { groupId: string; add: string[]; remove: string[] },
) => apiSend('POST', sciezka(cel, id), payload, z.object({ opId: z.string() }));

/**
 * SŁOWNIK KODÓW WYSPY — bez celu, dla okna ZAKŁADANIA grupy.
 *
 * ⚠️ POZOSTAŁE ODCZYTY WYMAGAJĄ ISTNIEJĄCEGO CELU (`/:id/operations`), a grupa, którą właśnie
 * tworzymy, nie ma jeszcze identyfikatora. Bez tej drogi uprawnienia można by zaznaczyć dopiero
 * PO zapisaniu — czyli w drugim kroku i w drugim oknie.
 */
export const fetchSlownikOperacji = (groupId: string) =>
  apiGet(`/employee-groups/operations/slownik?groupId=${groupId}`, z.array(z.string()));
